import { getWithAuthentication } from './api'
import { BASE_URL, CHAT_SESSIONS_ENDPOINT, CHAT_SCENARIO_WS_BASE_URL, CHAT_WARM_UP_ENDPOINT } from '@/assets/constants/api_endpoints'
import {
  CHAT_MESSAGE_TYPE_USER_MESSAGE,
  CHAT_MESSAGE_TYPE_USER_MESSAGE_AUDIO,
  CHAT_MESSAGE_TYPE_SESSION_CREATED,
  CHAT_MESSAGE_TYPE_SESSION_END,
  CHAT_MESSAGE_TYPE_ERROR,
  CHAT_WEEKLY_LIMIT_MSG,
  CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT,
} from '@/assets/constants/constants'

/**
 * WebSocket connection manager for chat sessions
 * Handles real-time message exchange with the backend
 */
class ChatSessionWebSocket {
  constructor(scenarioId, token) {
    this.scenarioId = scenarioId
    this.token = token
    this.ws = null
    this.messageHandlers = []
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 3
    this.reconnectDelay = 3000
    this.isConnected = false
  }

  /**
   * Establish WebSocket connection to the backend
   * @returns {Promise<void>}
   */
  async connect() {
    return new Promise((resolve, reject) => {
      try {
        
        const wsUrl = `${CHAT_SCENARIO_WS_BASE_URL}${this.scenarioId}/?token=${this.token}`
        console.log('[WebSocket] Connecting to:', wsUrl)
        this.ws = new WebSocket(wsUrl)

        this.ws.onopen = () => {
          console.log('[WebSocket] Connected to chat session')
          this.isConnected = true
          this.reconnectAttempts = 0
          resolve()
        }

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.notifyHandlers(message)
          } catch (error) {
            console.error('[WebSocket] Failed to parse message:', error)
          }
        }

        this.ws.onerror = (error) => {
          console.error('[WebSocket] Connection error:', error)
          reject(error)
        }

        this.ws.onclose = (event) => {
          console.log('[WebSocket] Connection closed', event?.code)
          this.isConnected = false
          if (event?.code !== CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT) {
            this.handleDisconnect()
          }
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Send a message through WebSocket
   * @param {Object} message - Message object to send
   * @returns {Promise<void>}
   */
  send(message) {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not connected'))
        return
      }

      try {
        this.ws.send(JSON.stringify(message))
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Send a user message in the chat
   * @param {string} messageText - The user's message text
   * @returns {Promise<void>}
   */
  sendUserMessage(messageText) {
    return this.send({
      type: CHAT_MESSAGE_TYPE_USER_MESSAGE,
      content: messageText,
      timestamp: new Date().toISOString()
    })
  }

  /**
   * Send a user audio message in the chat
   * @param {string} audioData - Base64-encoded audio data
   * @param {number} durationMs - Duration of audio in milliseconds
   * @returns {Promise<void>}
   */
  sendUserAudioMessage(audioData, durationMs) {
    return this.send({
      type: CHAT_MESSAGE_TYPE_USER_MESSAGE_AUDIO,
      audio_data: audioData,
      duration_ms: durationMs,
      timestamp: new Date().toISOString()
    })
  }

  /**
   * Send session end signal
   * @returns {Promise<void>}
   */
  sendSessionEnd() {
    return this.send({
      type: CHAT_MESSAGE_TYPE_SESSION_END,
      timestamp: new Date().toISOString()
    })
  }

  /**
   * Register a callback handler for incoming messages
   * @param {Function} handler - Callback function that receives message object
   */
  onMessage(handler) {
    this.messageHandlers.push(handler)
  }

  /**
   * Unregister a message handler
   * @param {Function} handler - Handler to remove
   */
  offMessage(handler) {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler)
  }

  /**
   * Notify all registered handlers of a message
   * @private
   * @param {Object} message - Message to notify about
   */
  notifyHandlers(message) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message)
      } catch (error) {
        console.error('[WebSocket] Handler error:', error)
      }
    })
  }

  /**
   * Handle WebSocket disconnection with reconnect logic
   * @private
   */
  handleDisconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      console.log(`[WebSocket] Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`)
      setTimeout(() => this.reconnect(), this.reconnectDelay)
    }
  }

  /**
   * Attempt to reconnect to the WebSocket
   * @private
   */
  async reconnect() {
    try {
      await this.connect()
    } catch (error) {
      console.error('[WebSocket] Reconnect failed:', error)
    }
  }

  /**
   * Close the WebSocket connection
   */
  disconnect() {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }
}

// Global WebSocket instance manager
let currentWebSocket = null

/**
 * Get or create a WebSocket connection for a chat session
 * @param {string} scenarioId - The scenario ID
 * @param {string} token - Authentication token
 * @returns {ChatSessionWebSocket} The WebSocket instance
 */
export function getChatSessionWebSocket(scenarioId, token) {
  if (!currentWebSocket || currentWebSocket.scenarioId !== scenarioId) {
    if (currentWebSocket) {
      currentWebSocket.disconnect()
    }
    currentWebSocket = new ChatSessionWebSocket(scenarioId, token)
  }
  return currentWebSocket
}

/**
 * Disconnect and cleanup the current WebSocket connection
 */
export function disconnectChatSessionWebSocket() {
  if (currentWebSocket) {
    currentWebSocket.disconnect()
    currentWebSocket = null
  }
}

/**
 * Call the Lambda warm-up endpoint before opening the WebSocket.
 * Degrades gracefully: a 502 is logged but does not block the caller.
 * A 401 propagates so the caller can redirect to login.
 *
 * @returns {Promise<void>}
 */
export async function warmUpLambda() {
  try {
    await getWithAuthentication(BASE_URL, CHAT_WARM_UP_ENDPOINT)
    console.log('[warmUpLambda] Lambda warm-up successful')
  } catch (error) {
    const status = error.response?.status
    if (status === 401) {
      throw error
    }
    // 502 or any other failure — log and continue
    console.warn('[warmUpLambda] Warm-up failed (non-blocking):', status, error.message)
  }
}

/**
 * Connect to a chat scenario and create a session via WebSocket.
 * The backend automatically creates a session when the connection is established.
 * The WebSocket connection is kept alive for the full chat session.
 *
 * @param {number} scenarioId - The scenario ID to connect to
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} The session object created by the backend
 * @throws {Error} If the connection fails or times out
 */
export async function connectAndCreateSession(scenarioId, token) {
  return new Promise((resolve, reject) => {
    try {
      const wsUrl = `${CHAT_SCENARIO_WS_BASE_URL}${scenarioId}/?token=${token}`

      const ws = new WebSocket(wsUrl)
      let sessionCreatedReceived = false
      let rejectedForWeeklyLimit = false
      const timeoutHandle = setTimeout(() => {
        if (!sessionCreatedReceived && !rejectedForWeeklyLimit) {
          ws.close()
          reject(new Error('Session creation timeout'))
        }
      }, 10000) // 10 second timeout

      ws.onopen = () => {
        console.log('[WebSocket] Connected to scenario endpoint, waiting for session creation...')
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          if (
            message.type === CHAT_MESSAGE_TYPE_ERROR &&
            message.message === CHAT_WEEKLY_LIMIT_MSG
          ) {
            rejectedForWeeklyLimit = true
            clearTimeout(timeoutHandle)
            const weeklyLimitError = new Error(CHAT_WEEKLY_LIMIT_MSG)
            weeklyLimitError.code = CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT
            ws.close(CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT, CHAT_WEEKLY_LIMIT_MSG)
            reject(weeklyLimitError)
            return
          }

          if (message.type === CHAT_MESSAGE_TYPE_SESSION_CREATED) {
            sessionCreatedReceived = true
            clearTimeout(timeoutHandle)
            console.log('[WebSocket] Session created:', message.data)
            
            // Create a ChatSessionWebSocket wrapper around the raw WebSocket
            const wsWrapper = new ChatSessionWebSocket(scenarioId, token)
            wsWrapper.ws = ws
            
            // Forward all incoming messages to the wrapper's handlers
            ws.onmessage = (evt) => {
              try {
                const msg = JSON.parse(evt.data)
                wsWrapper.notifyHandlers(msg)
              } catch (error) {
                console.error('[WebSocket] Failed to parse message:', error)
              }
            }
            
            // Store the wrapper as the global singleton
            currentWebSocket = wsWrapper
            
            // Resolve with session data - the connection stays alive
            resolve(message.data)
          }
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error)
        }
      }

      ws.onerror = (error) => {
        clearTimeout(timeoutHandle)
        if (!sessionCreatedReceived && !rejectedForWeeklyLimit) {
          ws.close()
          reject(new Error('WebSocket connection error: ' + error.message))
        }
      }

      ws.onclose = (event) => {
        clearTimeout(timeoutHandle)
        if (sessionCreatedReceived || rejectedForWeeklyLimit) {
          return
        }
        if (event?.code === CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT) {
          const weeklyLimitError = new Error(CHAT_WEEKLY_LIMIT_MSG)
          weeklyLimitError.code = CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT
          reject(weeklyLimitError)
          return
        }
        if (!sessionCreatedReceived) {
          reject(new Error('WebSocket closed before session was created'))
        }
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Gets all chat sessions for the current user via HTTP.
 *
 * Makes an authenticated GET request to retrieve session history.
 *
 * @returns {Promise<Array>} Array of session objects
 * @throws {Error} If the request fails
 */
export async function getChatSessions() {
  const response = await getWithAuthentication(BASE_URL, CHAT_SESSIONS_ENDPOINT)
  return response?.data || []
}
